/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

import React, { useState, useEffect } from "react";
import {
    Box,
    FormControl,
    FormLabel,
    Input,
    Flex,
    Text,
    VStack,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useColorModeValue,
    useDisclosure,
    FormErrorMessage,
    useToast,
} from "@chakra-ui/react";
import { useGetCurrentUserQuery, useUpdateCurrentUserMutation } from "store";
import { Helmet } from "react-helmet-async";

const UserProfile = () => {
    const { data: user, isLoading, isError } = useGetCurrentUserQuery();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [
        updateUser,
        { isLoading: isUpdating, error: updateError },
    ] = useUpdateCurrentUserMutation();
    const toast = useToast();

    // form state
    const [values, setValues] = useState({
        first_name: "",
        last_name: "",
        username: "",
        email: "",
    });
    const [errors, setErrors] = useState({});

    // initialize when user loads
    useEffect(() => {
        if (user) {
            setValues({
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                username: user.username || "",
                email: user.email || "",
            });
        }
    }, [user]);

    // simple validation function
    const validate = () => {
        const newErrors = {};
        if (!values.first_name.trim())
            newErrors.first_name = "First name is required";
        if (!values.last_name.trim())
            newErrors.last_name = "Last name is required";
        if (!values.username.trim())
            newErrors.username = "Username is required";
        if (!values.email) {
            newErrors.email = "Email is required";
        } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
        ) {
            newErrors.email = "Invalid email address";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setValues((v) => ({ ...v, [name]: value }));
        setErrors((e) => ({ ...e, [name]: undefined })); // clear error for the field being edited
    };

    const handleUpdate = async (event) => {
        event.preventDefault();
        if (!validate()) return; // validate before submitting
        try {
            await updateUser(values).unwrap();
            toast({
                title: "Profile updated.",
                description: "Your profile has been updated successfully.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            onClose(); // close modal after successful update
        } catch (error) {
            toast({
                title: "Error updating profile.",
                description: error?.data?.message || "An error occurred.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const bg = useColorModeValue("white", "gray.700");

    if (isLoading) return <Box>Loading...</Box>;
    if (isError || !user) return <Box>Error fetching user data.</Box>;

    const userRole = user.is_hospital_admin
        ? "Admin"
        : user.is_receptionist
        ? "Receptionist"
        : "Standard User";

    const formattedDate = new Date(user.last_login).toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        timeZoneName: "short",
    });

    return (
        <>
            <Helmet>
                <title>User Profile | GEMHS </title>
            </Helmet>

            <Box
                mt={{ base: "10", md: "8" }}
                pt={{ base: "4", md: "8" }}
                pb={{ base: "8", md: "10" }}
                mx="auto"
                maxW="lg"
                px={4}
            >
                <Box bg={bg} p={6} rounded="md" shadow="base">
                    <Flex justify="space-between" align="center" mb={4}>
                        <Text fontSize="2xl" fontWeight="bold">
                            User Profile
                        </Text>
                        <Button size="sm" onClick={onOpen}>
                            Edit
                        </Button>
                    </Flex>

                    <VStack spacing={3} align="stretch">
                        <Flex>
                            <Text fontWeight="medium" w="40%">
                                First Name
                            </Text>
                            <Text>{user.first_name || "—"}</Text>
                        </Flex>

                        <Flex>
                            <Text fontWeight="medium" w="40%">
                                Last Name
                            </Text>
                            <Text>{user.last_name || "—"}</Text>
                        </Flex>

                        <Flex>
                            <Text fontWeight="medium" w="40%">
                                Username
                            </Text>
                            <Text>{user.username}</Text>
                        </Flex>

                        <Flex>
                            <Text fontWeight="medium" w="40%">
                                Email
                            </Text>
                            <Text>{user.email}</Text>
                        </Flex>

                        <Flex>
                            <Text fontWeight="medium" w="40%">
                                Role
                            </Text>
                            <Text>{userRole}</Text>
                        </Flex>

                        <Flex>
                            <Text fontWeight="medium" w="40%">
                                Last Login
                            </Text>
                            <Text>{formattedDate}</Text>
                        </Flex>
                    </VStack>
                </Box>
            </Box>

            {/* Modal for updating user information */}
            <Modal 
                isOpen={isOpen} 
                onClose={onClose} 
                isCentered
                closeOnOverlayClick={false}
                closeOnEsc={false} 
            >
                <ModalOverlay />
                <ModalContent mx={4}>
                    <ModalHeader>Edit Profile</ModalHeader>
                    <Box as="form" onSubmit={handleUpdate}>
                        <ModalBody>
                            <VStack spacing={4}>
                                <FormControl isInvalid={!!errors.first_name}>
                                    <FormLabel>First Name</FormLabel>
                                    <Input
                                        name="first_name"
                                        value={values.first_name}
                                        onChange={handleChange}
                                    />
                                    <FormErrorMessage>
                                        {errors.first_name}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={!!errors.last_name}>
                                    <FormLabel>Last Name</FormLabel>
                                    <Input
                                        name="last_name"
                                        value={values.last_name}
                                        onChange={handleChange}
                                    />
                                    <FormErrorMessage>
                                        {errors.last_name}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={!!errors.username}>
                                    <FormLabel>Username</FormLabel>
                                    <Input
                                        name="username"
                                        value={values.username}
                                        onChange={handleChange}
                                    />
                                    <FormErrorMessage>
                                        {errors.username}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={!!errors.email}>
                                    <FormLabel>Email</FormLabel>
                                    <Input
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                    />
                                    <FormErrorMessage>
                                        {errors.email}
                                    </FormErrorMessage>
                                </FormControl>
                            </VStack>
                        </ModalBody>

                        <ModalFooter>
                            <Button
                                colorScheme="blue"
                                mr={3}
                                type="submit"
                                isLoading={isUpdating}
                                isDisabled={isUpdating}
                            >
                                Save Changes
                            </Button>
                            <Button onClick={onClose} variant="ghost">
                                Cancel
                            </Button>
                        </ModalFooter>
                    </Box>
                </ModalContent>
            </Modal>
        </>
    );
};

export default UserProfile;
